import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import back from "../../../assets/icons/back.svg";
import bus from "../../../assets/icons/bus.svg";

import ExtraChargeModal from "app/components/BookingsAndReferrals/ExtraChargeModal";
import InfoItem from "app/components/BookingsAndReferrals/InfoItem";
import IssueRefundModal from "app/components/BookingsAndReferrals/IssueRefundModal";
import TipRefereeModal from "app/components/BookingsAndReferrals/TipRefereeModal";
import NavBar from "app/components/NavBar";
import { useCancelBookingMutation, useGetBookingDetailsQuery } from "lib/features/booking/bookingService";
import { useState } from "react";
import calender from "../../../assets/icons/calendar-dollar.svg";
import calenderSimple from "../../../assets/icons/cali.svg";
import email from "../../../assets/icons/email.svg";
import ticket from "../../../assets/icons/newticket.svg";
import phone from "../../../assets/icons/phone.svg";
import ClockIcon from "../../../assets/icons/time.svg";
import user_avatar from "../../../assets/icons/user_avatar.svg";

const baseUrl = "https://applicationimages.s3.amazonaws.com";

const BookingDetails = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { id } = useParams(); // Get booking ID from URL

  // API call to get booking details
  const {
    data: bookingResponse,
    isLoading,
    isError,
  } = useGetBookingDetailsQuery(id ?? "", { skip: !id });

  const [cancelBooking, { isLoading: isCanceling }] = useCancelBookingMutation();

   const handleCancel = async () => {
    if (!id) return;

    try {
      await cancelBooking(id).unwrap();
      console.log("Booking canceled successfully!");
      navigate(-1); 
    } catch (error) {
      console.error(error);
      console.log("Failed to cancel booking.");
    }
  };

  const [isExtraChargeModalOpen, setIsExtraChargeModalOpen] = useState(false);
  const [isTipRefereeModalOpen, setIsTipRefereeModalOpen] = useState(false);
  const [isIssueRefundModalOpen, setIsIssueRefundModalOpen] = useState(false);

  // extra charge
  const handleOpenExtraChargeModal = () => setIsExtraChargeModalOpen(true);
  const handleCloseExtraChargeModal = () => setIsExtraChargeModalOpen(false);

  // tip referee
  const handleOpenTipRefereeModal = () => setIsTipRefereeModalOpen(true);
  const handleCloseTipRefereeModal = () => setIsTipRefereeModalOpen(false);

  // issue refund
  const handleOpenIssueRefundModal = () => setIsIssueRefundModalOpen(true);
  const handleCloseIssueRefundModal = () => setIsIssueRefundModalOpen(false);

  // Helper function to get the best available image
  const getImageUri = () => {
    if (!bookingResponse?.data) return bus;

    const booking = bookingResponse.data;
    
    // First try to get active item images
    const activeItemImages = booking.item?.itemImages?.filter((img: { record_status: boolean; }) => img.record_status === true) || [];
    if (activeItemImages.length > 0) {
      return `${baseUrl}/${activeItemImages[0].uri}`;
    }
    
    // If no active item images, try business images
    const activeBusinessImages = booking.referral?.business?.businessImages?.filter((img: { record_status: boolean; }) => img.record_status === true) || [];
    if (activeBusinessImages.length > 0) {
      return `${baseUrl}/${activeBusinessImages[0].uri}`;
    }
    
    // Fallback to default bus image
    return bus;
  };

  // Utility functions
  const formatDate = (dateString: Date) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (dateString: Date) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    const num = Number(amount);

    if (isNaN(num)) return "$0.00";

    return `$${num.toFixed(2)}`;
  };

  // Loading state
  if (isLoading) {
    return (
      <>
        <NavBar />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography>Loading booking details...</Typography>
        </Box>
      </>
    );
  }

  // Error state
  if (isError || !bookingResponse?.data) {
    return (
      <>
        <NavBar />
        <Box sx={{ margin: "20px" }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {"Failed to load booking details"}
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate(-1)}
            sx={{ textTransform: "none" }}
          >
            Go Back
          </Button>
        </Box>
      </>
    );
  }

  // Extract data from API response
  const booking = bookingResponse.data;
  const itemDetails = booking.referral?.item?.itemDetails?.[0];
  const refereeUser = booking.referral?.user;
  const business = booking.referral?.business;

  return (
    <>
      <NavBar />
      <Box
        sx={{
          marginTop: "10px",
          fontFamily: theme.typography.fontFamily,
          margin: "20px 20px",
        }}
      >
        {/* Back Button */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => navigate(-1)}
        >
          <img src={back} width="18px" alt="back" />
          <Typography
            sx={{ marginLeft: "5px", fontSize: "17px", fontWeight: "600" }}
          >
            Back
          </Typography>
        </Box>

        {/* Main Info Container */}
        <Box
          sx={{
            width: isMobile ? "90%" : "640px",
            margin: "20px auto",
            // borderRadius: "8px",
            backgroundColor: "#fff",
          }}
        >
          {/* Image Section */}
          <Box
            component="img"
            src={getImageUri()}
            alt="tour"
            sx={{
              width: "100%",
              height: "auto",
              borderRadius: "8px",
              marginBottom: "15px",
            }}
          />

          {/* Title */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: isMobile ? "20px" : "32px",
            }}
          >
            {itemDetails?.item_name || "Booking Details"}
          </Typography>

          {/* Subtitle/Headline */}
          {itemDetails?.headline && (
            <Typography
              sx={{
                fontSize: isMobile ? "14px" : "16px",
                color: "text.secondary",
                mb: 3,
              }}
            >
              {itemDetails.headline}
            </Typography>
          )}

          {/* Item Details Section */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              fontSize: isMobile ? "18px" : "24px",
              margin: "40px 0 8px",
            }}
          >
            Item Details
          </Typography>

          <InfoItem
            image={ticket}
            title={`${booking.tickets_bought || 0} Ticket${
              booking.tickets_bought !== 1 ? "s" : ""
            }`}
          />

          <InfoItem
            image={calenderSimple}
            value={
              booking.reservation_time
                ? formatDate(booking.reservation_time)
                : "N/A"
            }
          />

          <InfoItem
            image={ClockIcon}
            title={"Time:"}
            value={
              booking.reservation_time
                ? `${formatTime(booking.reservation_time)} · ${
                    itemDetails?.duration || "2.5"
                  } hours`
                : "N/A"
            }
          />

          <InfoItem
            image={calender}
            title={"Purchase Date:"}
            value={
              booking.booking_time ? formatDate(booking.booking_time) : "N/A"
            }
          />

          {/* Add-Ons Section */}
          {itemDetails && (
            <>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  fontSize: isMobile ? "18px" : "24px",
                  margin: "40px 0 8px",
                }}
              >
                Options
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {itemDetails.max_guest && (
                  <>
                    <InfoItem title={"How many people are in your group?"} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography>
                        Up to {itemDetails.max_guest} People
                      </Typography>
                      <Typography>
                        {formatCurrency(itemDetails.price)}
                      </Typography>
                    </Box>
                  </>
                )}

                {itemDetails.whats_included && (
                  <>
                    <InfoItem title={"What's included?"} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "14px", color: "text.secondary" }}
                      >
                        {itemDetails.whats_included}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </>
          )}

          {/* Price Details Section */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              marginBottom: "8px",
              fontSize: isMobile ? "18px" : "24px",
              mt: "40px",
            }}
          >
            Price Details
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "17px" }}>
              Customer Total
            </Typography>
            <Typography sx={{ fontWeight: "bold", fontSize: "17px" }}>
              {formatCurrency(booking.customer_total)}
            </Typography>
          </Box>

          <Divider />
          <Box
            sx={{ display: "flex", justifyContent: "space-between", my: "8px" }}
          >
            <Typography sx={{ fontWeight: "bold", fontSize: "17px" }}>
              Total
            </Typography>
            <Typography sx={{ fontWeight: "bold", fontSize: "17px" }}>
              {formatCurrency(booking.customer_total)}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography>Commission(10%)</Typography>
            <Typography>
              -{formatCurrency(booking.commission_payout)}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography>Platform fee</Typography>
            <Typography>-{formatCurrency(booking.platform_fee)}</Typography>
          </Box>
          <Divider />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              my: "11px",
              fontWeight: 600,
            }}
          >
            <Typography sx={{ fontWeight: "600", fontSize: "17px" }}>
              Total Payout
            </Typography>
            <Typography sx={{ fontWeight: "600", fontSize: "17px" }}>
              {formatCurrency(booking.business_payout)}
            </Typography>
          </Box>
          <Divider />

          {/* Additional Info Section */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              margin: "40px 0 8px",
              fontSize: isMobile ? "18px" : "24px",
            }}
          >
            Customer Info
          </Typography>

          {booking.comment && (
            <Box sx={{ display: "flex", flexDirection: "column", mb: 1 }}>
              <InfoItem title={"Comments"} />
              <InfoItem value={booking.comment} />
            </Box>
          )}

          {itemDetails?.description && (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <InfoItem title={"Experience Description:"} />
              <InfoItem value={itemDetails.description} />
            </Box>
          )}

          {/* Customer Details Section */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              margin: "40px 0 8px",
              fontSize: isMobile ? "20px" : "24px",
            }}
          >
            Customer Details
          </Typography>

          <InfoItem
            image={user_avatar}
            value={booking.customer_name || "N/A"}
          />
          <InfoItem image={phone} value={booking.customer_phone || "N/A"} />
          <InfoItem image={email} value={booking.customer_email || "N/A"} />
          <Typography sx={{ fontSize: "17px" }}>
            <strong style={{ color: "green", textDecoration: "underline" }}>
              Send Confirmation Email
            </strong>
          </Typography>

          {/* Referee Details Section */}
          {refereeUser && (
            <>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  margin: "40px 0 8px",
                  fontSize: isMobile ? "20px" : "24px",
                }}
              >
                Referee Details
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <img
                    src={user_avatar}
                    alt="user_avatar"
                    style={{ width: "20px", marginRight: "5px" }}
                  />
                  <Typography sx={{ fontSize: "17px" }}>
                    <strong>Referee:</strong>{" "}
                    <strong
                      style={{ color: "green", textDecoration: "underline" }}
                    >
                      {`${refereeUser.first_name || ""} ${
                        refereeUser.last_name || ""
                      }`.trim()}
                    </strong>
                  </Typography>
                </Box>

                {business && (
                  <>
                    <InfoItem title={"Business:"} value={business.business} />
                    <InfoItem
                      title={"Business Phone:"}
                      value={business.phone}
                    />
                  </>
                )}

                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#286C2A",
                    color: "white",
                    textTransform: "none",
                    marginTop: "10px",
                    width: { xs: "100%", sm: "48%" },
                    "&:hover": { backgroundColor: "darkgreen" },
                  }}
                  onClick={handleOpenTipRefereeModal}
                >
                  Tip Referee
                </Button>
              </Box>
            </>
          )}

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              // flexDirection: "row",
              justifyContent: "start",
              gap: "20px",
              marginTop: "30px",
              width: "100%",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Button
              variant="outlined"
              sx={{
                textTransform: "none",
                borderColor: "black",
                color: "black",
                "&:hover": { borderColor: "black" },
                width: "100%",
              }}
              onClick={handleOpenIssueRefundModal}
            >
              Issue Refund
            </Button>
            <Button
              variant="outlined"
              sx={{
                textTransform: "none",
                borderColor: "black",
                color: "black",
                "&:hover": { borderColor: "black" },
                width: "100%",
              }}
              onClick={handleOpenExtraChargeModal}
            >
              Add Extra Charge
            </Button>
          </Box>

          <Button
            sx={{
              textTransform: "none",
              borderColor: "black",
              backgroundColor: "#B2201D",
              color: "white",
              width: "100%",
              marginTop: "20px",
              "&:hover": { backgroundColor: "#860603ff" },
            }}
             onClick={handleCancel}  
             disabled={isCanceling}   
          >
            Cancel Booking
          </Button>
        </Box>

        {/* Modals */}

        {/* Extra Refund Modal */}

        <ExtraChargeModal
          open={isExtraChargeModalOpen}
          close={handleCloseExtraChargeModal}
          // customStyle={{ bgcolor: "lightblue" }}
          sx={{}}
        />

        <TipRefereeModal
          open={isTipRefereeModalOpen}
          close={handleCloseTipRefereeModal}
          // customStyle={{ bgcolor: "lightblue" }}
          sx={{}}
        />

        <IssueRefundModal
          open={isIssueRefundModalOpen}
          close={handleCloseIssueRefundModal}
          // customStyle={{ bgcolor: "lightblue" }}
          sx={{}}
        />
      </Box>
    </>
  );
};

export default BookingDetails;